import { useEffect, useMemo, useState } from 'react';
import {
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  MessageBarActions,
  Button,
  makeStyles,
} from '@fluentui/react-components';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { monthRemaining } from '../api/usage.js';
import {
  approxFromRemaining,
  fallbackPlanEquivalence,
} from '../lib/planEquivalence.js';
import {
  tokenAlertCopy,
  tokenAlertLevel,
  tokenAlertPeriod,
  tokenAlertStorageKey,
  tokenAlertUserKey,
  type TokenAlertLevel,
} from '../lib/tokenAlerts.js';
import { useAuthStore } from '../stores/authStore.js';
import { useUsageStore } from '../stores/usageStore.js';
import { TiendaDialog } from './TiendaDialog.js';
import { enCreditos } from '../lib/creditos.js';

const useStyles = makeStyles({
  wrap: {
    padding: '8px var(--space-xxl) 10px',
    borderBottom: '1px solid var(--border-subtle)',
    backgroundColor: 'var(--bg-header)',
  },
});

function readDismissed(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(key: string): void {
  try {
    localStorage.setItem(key, '1');
  } catch {
    /* private mode / quota */
  }
}

/**
 * Aviso 75 / 90 / 100 de la bolsa mensual, debajo de la barra superior.
 * Un descarte por usuario+mes+nivel (localStorage). No se monta en demo.
 */
export function TokenAlertBanner() {
  const styles = useStyles();
  const usage = useUsageStore((s) => s.data);
  const { demoGuest, user, tenant } = useAuthStore((s) => ({
    demoGuest: s.demoGuest,
    user: s.user,
    tenant: s.tenant,
  }));
  const [tiendaOpen, setTiendaOpen] = useState(false);
  const [dismissedHere, setDismissedHere] = useState(false);

  const demoBuild = import.meta.env.VITE_DEMO === '1';
  const used = usage?.tokens?.used ?? 0;
  const quota = usage?.tokens?.quota ?? 0;
  const level = usage ? tokenAlertLevel(used, quota) : 0;

  const userKey = tokenAlertUserKey({
    email: user?.email,
    userId: user?.id,
    plan: usage?.plan,
    tenantId: tenant?.id ?? tenant?.slug,
  });
  const yearMonth = tokenAlertPeriod(usage);
  const storageKey =
    level === 0 ? '' : tokenAlertStorageKey(userKey, yearMonth, level);

  useEffect(() => {
    setDismissedHere(false);
  }, [storageKey]);

  const remaining = monthRemaining(usage);
  const approx = useMemo(() => {
    if (!usage) return { concepts: 0, apus: 0, chats: 0 };
    const eq = usage.plan_equivalence ?? fallbackPlanEquivalence(usage.plan, usage.seats ?? 1);
    return approxFromRemaining(remaining, {
      concepts: eq.concepts,
      apus: eq.apus,
      chats: eq.chats,
      tokens_month: eq.tokens_month || eq.tokens_week || quota || 1,
    });
  }, [usage, remaining, quota]);

  if (demoGuest || demoBuild) return null;
  if (!usage || level === 0 || !storageKey) return null;
  if (dismissedHere || readDismissed(storageKey)) return null;

  const cr = enCreditos(usage);
  const copy = tokenAlertCopy(level as Exclude<TokenAlertLevel, 0>, remaining, approx, cr ? 'creditos' : 'tokens');
  const intent = level === 75 ? 'warning' : 'error';

  return (
    <>
      <div className={styles.wrap}>
        <MessageBar intent={intent} layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>{copy.title}</MessageBarTitle> {copy.body}
          </MessageBarBody>
          <MessageBarActions
            containerAction={
              <Button
                appearance="transparent"
                icon={<Dismiss20Regular />}
                aria-label="Cerrar aviso"
                onClick={() => {
                  writeDismissed(storageKey);
                  setDismissedHere(true);
                }}
              />
            }
          >
            <Button appearance="primary" size="small" onClick={() => setTiendaOpen(true)}>
              {cr ? 'Recargar créditos' : 'Recargar tokens'}
            </Button>
          </MessageBarActions>
        </MessageBar>
      </div>
      <TiendaDialog open={tiendaOpen} onClose={() => setTiendaOpen(false)} />
    </>
  );
}
